import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { getAppStore, destroyAppStore, IAppStore } from './AppStore';

/**
 * UNIFIED STORE PROVIDER
 * Provides the single AppStore instance to all components.
 * 
 * IMPORTANT: Store is ONLY created after Stytch session is validated
 * This prevents creating stores before user is authenticated
 * 
 * On signout, the store is destroyed completely
 * 
 * NOT wrapped in observer() to prevent re-render loops
 */

// Create context for the unified store
const StoreContext = createContext<IAppStore | null>(null);

// Provider component
export const UnifiedStoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { session, isInitialized: stytchInitialized } = useStytchMemberSession();
  const [store, setStore] = useState<IAppStore | null>(null);
  
  useEffect(() => {
    const pathname = window.location.pathname;
    const publicRoutes = ['/login', '/signup', '/auth'];
    const isPublicRoute = publicRoutes.some(route => pathname.startsWith(route));
    
    console.log('🔍 [UnifiedStoreProvider] Checking auth requirements:', {
      pathname,
      isPublicRoute,
      hasSession: !!session,
      stytchInitialized,
      storeExists: !!store
    });
    
    // If Stytch has initialized and we're checking
    if (stytchInitialized) {
      // If NO session and NOT on public route → redirect to login immediately
      if (!session && !isPublicRoute) {
        console.log('🚫 [UnifiedStoreProvider] No Stytch session on protected route, redirecting to /login');
        window.location.href = '/login';
        return;
      }
      
      // If we have a session and no store yet, create it
      if (session && !store) {
        console.log('✅ [UnifiedStoreProvider] Valid Stytch session, creating store...');
        setStore(getAppStore());
      }
      
      // If session is lost and we have a store, destroy it
      if (!session && store) {
        console.log('🚫 [UnifiedStoreProvider] Session lost, destroying store...');
        destroyAppStore();
        setStore(null);
      }
    }
  }, [session, stytchInitialized]); // Removed `store` from deps to prevent infinite loop
  
  // While waiting for Stytch to initialize, show loading screen
  if (!stytchInitialized) {
    console.log('⏳ [UnifiedStoreProvider] Waiting for Stytch to initialize...');
    return (
      <div className="app-initializer-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  // On public routes, we can render without a store
  const pathname = window.location.pathname;
  const publicRoutes = ['/login', '/signup', '/auth'];
  const isPublicRoute = publicRoutes.some(route => pathname.startsWith(route));
  
  // On protected routes, wait for store to be created
  if (!isPublicRoute && !store) {
    console.log('⏳ [UnifiedStoreProvider] Protected route but no store yet, waiting...');
    return (
      <div className="app-initializer-loading">
        <div className="loading-spinner"></div>
        <p>Initializing...</p>
      </div>
    );
  }
  
  // Provide store (might be null on public routes - that's OK, they don't use it)
  return (
    <StoreContext.Provider value={store}>
      {children}
    </StoreContext.Provider>
  );
};

// The one and only hook to access the unified store
export const useAppStore = (): IAppStore | null => {
  const store = useContext(StoreContext);
  if (!store) {
    // Store might be null on public routes or before Stytch session is validated
    const pathname = window.location.pathname;
    const publicRoutes = ['/login', '/signup', '/auth'];
    const isPublicRoute = publicRoutes.some(route => pathname.startsWith(route));
    
    if (isPublicRoute) {
      // On public routes, store might not be created yet - that's OK
      // Return null instead of throwing
      return null;
    }
    
    throw new Error('AppStore not available - Stytch session may not be validated yet');
  }
  return store;
};

// Legacy compatibility exports
export const rootStore = getAppStore(); // Use getter for lazy load
export const useRootStore = useAppStore;

export default UnifiedStoreProvider;
